import { Controller } from '@hotwired/stimulus'

export default class PurchaseCheckoutController extends Controller {
  static targets = [
    'acceptOverpriceCheckbox',
    'submitButton',
    'form',
    'signButton',
    'retryButton',
    'retryButtonText',
    'retryButtonSpinner',
  ];

  static values = {
    overpriced: Boolean,
    missingAmount: Number,
    errorMessage: String,
    signatureErrorMessage: String,
    pendingPurchaseId: Number,
  };

  connect() {
    if (this.pendingPurchaseIdValue) {
      this.currentPurchaseId = this.pendingPurchaseIdValue;
      this.submitButtonTarget.classList.add('d-none');
      this.#setRetryButtonLoading();
      this.#subscribeToSignatureChannel(this.pendingPurchaseIdValue);
    } else {
      this.#updateButton();
    }
  }

  disconnect() {
    this.#clearSignatureTimeout();
  }

  onCheckboxChange() {
    this.#updateButton();
  }

  prefillPayinModal() {
    if (this.missingAmountValue <= 0 || this.missingAmountValue > 500) return;

    var modalElement = document.getElementById('payinModal');
    if (modalElement) {
      modalElement.dataset.prefillAmount = this.missingAmountValue;
    }
  }

  retrySignature(event) {
    event.preventDefault();
    this.#setRetryButtonLoading();
    this.#subscribeToSignatureChannel(this.currentPurchaseId);
  }

  submitPurchase(event) {
    event.preventDefault();
    if (!this.#allChecked()) return;

    this.submitButtonTarget.classList.add('d-none');
    this.#setRetryButtonLoading();

    const formData = new FormData(this.formTarget);

    fetch(this.formTarget.action, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
      },
    })
      .then((response) => {
        if (response.ok) return response.json();
        throw new Error('purchase_failed');
      })
      .then((data) => {
        this.currentPurchaseId = data.purchase_id;
        this.#subscribeToSignatureChannel(data.purchase_id);
      })
      .catch(() => {
        this.#showRetryButton();
        toastr.error(this.errorMessageValue);
      });
  }

  #subscribeToSignatureChannel(purchaseId) {
    const controller = this;

    this.#clearSignatureTimeout();
    this.signatureTimeout = setTimeout(() => {
      controller.#handleSignatureError();
      if (controller.signatureSubscription) {
        controller.signatureSubscription.unsubscribe();
        controller.signatureSubscription = null;
      }
    }, 20000);

    this.signatureSubscription = App.cable.subscriptions.create(
      {
        channel: 'SecondaryMarketPurchaseSignatureChannel',
        purchase_id: purchaseId,
      },
      {
        received(data) {
          controller.#clearSignatureTimeout();
          if (data.status === 'success') {
            controller.#showSignButton(data.transaction_url);
          } else {
            controller.#handleSignatureError();
          }
          this.unsubscribe();
          controller.signatureSubscription = null;
        },
      }
    );
  }

  #clearSignatureTimeout() {
    if (this.signatureTimeout) {
      clearTimeout(this.signatureTimeout);
      this.signatureTimeout = null;
    }
  }

  #handleSignatureError() {
    this.#showRetryButton();
    toastr.error(this.signatureErrorMessageValue);
  }

  #setRetryButtonLoading() {
    this.retryButtonTarget.disabled = true;
    this.retryButtonTarget.classList.remove('d-none');
    this.retryButtonTextTarget.classList.add('d-none');
    this.retryButtonSpinnerTarget.classList.remove('d-none');
  }

  #showRetryButton() {
    this.retryButtonTarget.disabled = false;
    this.retryButtonTextTarget.classList.remove('d-none');
    this.retryButtonSpinnerTarget.classList.add('d-none');
    this.retryButtonTarget.classList.remove('d-none');
  }

  goToSignature() {
    if (this.signatureUrl) {
      window.location.href = this.signatureUrl;
    }
  }

  #showSignButton(url) {
    this.signatureUrl = url;
    this.submitButtonTarget.classList.add('d-none');
    if (this.hasRetryButtonTarget) {
      this.retryButtonTarget.classList.add('d-none');
    }
    if (this.hasSignButtonTarget) {
      this.signButtonTarget.classList.remove('d-none');
    }
  }

  #updateButton() {
    if (!this.hasSubmitButtonTarget) return;

    this.submitButtonTarget.disabled = !this.#allChecked();
  }

  #allChecked() {
    if (this.overpricedValue && this.hasAcceptOverpriceCheckboxTarget) {
      return this.acceptOverpriceCheckboxTarget.checked;
    }

    return true;
  }
}
